import {
  createOrganizationAuthStorage,
  getCurrentOrganizationAuth,
  OrganizationAuth,
  OrganizationAuthStorage,
} from './auth-org';
import { getUri } from './config';
import {
  INVALID_SESSION_TOKEN_DETAIL,
  SESSION_TOKEN_EXPIRED_DETAIL,
} from './consts';
import { NOT_FOUND_STATUS_CODE, UNAUTHORIZED_STATUS_CODE } from './http';
import {
  ExponentialBackoff,
  ExponentialBackoffOptions,
  isErrorCode,
  isErrorCodeRetryable,
  RETRYABLE_ERROR_MESSAGE,
} from './retries';

export interface FlikHttpAuthOptions {
  headerName: string;
  token: {
    accessTokenKey: string;
    refreshTokenKey: string;
  };
  skiplist?: {
    paths?: string[];
    graphql?: string[];
  };
  storage?: {
    get: (key: string) => string | null;
    set: (key: string, value: string) => void;
    remove: (key: string) => void;
  };
  // Organization-aware auth storage
  organizationStorage?: OrganizationAuthStorage;
}

export const FlikHttpAuth = (options: FlikHttpAuthOptions) => {
  // Use organization storage if available, otherwise fall back to legacy storage
  const orgStorage =
    options.organizationStorage || createOrganizationAuthStorage();

  const getAccessToken = (): string | null => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const currentOrgAuth = getCurrentOrganizationAuth(orgStorage);
      return currentOrgAuth?.accessToken || null;
    }

    // Fall back to legacy storage
    if (options.storage) {
      return options.storage.get(options.token.accessTokenKey);
    }
    return localStorage.getItem(options.token.accessTokenKey);
  };

  const hasAccessToken = (): boolean => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const currentOrgAuth = getCurrentOrganizationAuth(orgStorage);
      return !!currentOrgAuth?.accessToken;
    }

    // Fall back to legacy storage
    if (options.storage) {
      return !!options.storage.get(options.token.accessTokenKey);
    }
    return !!localStorage.getItem(options.token.accessTokenKey);
  };

  const getRefreshToken = (): string | null => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const currentOrgAuth = getCurrentOrganizationAuth(orgStorage);
      return currentOrgAuth?.refreshToken || null;
    }

    // Fall back to legacy storage
    if (options.storage) {
      return options.storage.get(options.token.refreshTokenKey);
    }
    return localStorage.getItem(options.token.refreshTokenKey);
  };

  const setAccessToken = (token: string) => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const currentOrgAuth = getCurrentOrganizationAuth(orgStorage);
      if (currentOrgAuth) {
        orgStorage.updateOrganization(currentOrgAuth.organization_id, {
          accessToken: token,
        });
        return;
      }
    }

    // Fall back to legacy storage
    if (options.storage) {
      options.storage.set(options.token.accessTokenKey, token);
    } else {
      localStorage.setItem(options.token.accessTokenKey, token);
    }
  };

  const setRefreshToken = (token: string) => {
    // Try organization-aware storage first
    if (options.organizationStorage) {
      const currentOrgAuth = getCurrentOrganizationAuth(orgStorage);
      if (currentOrgAuth) {
        orgStorage.updateOrganization(currentOrgAuth.organization_id, {
          refreshToken: token,
        });
        return;
      }
    }

    // Fall back to legacy storage
    if (options.storage) {
      options.storage.set(options.token.refreshTokenKey, token);
    } else {
      localStorage.setItem(options.token.refreshTokenKey, token);
    }
  };

  // New methods for organization-aware auth
  const setOrganizationAuth = (
    organizationId: string,
    auth: OrganizationAuth,
  ) => {
    if (options.organizationStorage) {
      orgStorage.updateOrganization(organizationId, auth);
    }
  };

  const setCurrentOrganization = (organizationId: string | null) => {
    if (options.organizationStorage) {
      orgStorage.setCurrentOrganization(organizationId);
    }
  };

  const getCurrentOrganization = (): string | null => {
    if (options.organizationStorage) {
      const data = orgStorage.get();
      return data?.currentOrganization || null;
    }
    return null;
  };

  const getAllOrganizations = () => {
    if (options.organizationStorage) {
      return orgStorage.get()?.organizations || [];
    }
    return [];
  };

  const authenticate = (
    email: string,
    verificationCode?: string,
    backoff?: ExponentialBackoffOptions,
    resendVerificationCode = false,
  ) => {
    const uri = getUri();
    return ExponentialBackoff(
      async () => {
        const response = await fetch(`${uri}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant: 'public',
            emailAddress: email,
            verificationCode,
            resendVerificationCode,
          }),
        });

        if (isErrorCodeRetryable(response.status)) {
          throw new Error(RETRYABLE_ERROR_MESSAGE);
        }

        if (isErrorCode(response.status)) {
          throw new Error((await response.json()).detail);
        }

        const data = await response.json();
        if (data.accessToken && data.refreshToken) {
          setAccessToken(data.accessToken);
          setRefreshToken(data.refreshToken);
        }

        return data;
      },
      (error) => error.message === RETRYABLE_ERROR_MESSAGE,
      backoff,
    );
  };

  const clearSession = async (backoff?: ExponentialBackoffOptions) => {
    const deleteTokens = () => {
      // Try organization-aware storage first
      if (options.organizationStorage) {
        const currentOrgAuth = getCurrentOrganizationAuth(orgStorage);
        if (currentOrgAuth) {
          orgStorage.removeOrganization(currentOrgAuth.organization_id);
        }
        return;
      }

      // Fall back to legacy storage
      if (options.storage) {
        if (options.storage.get(options.token.accessTokenKey)) {
          options.storage.remove(options.token.accessTokenKey);
        }
        if (options.storage.get(options.token.refreshTokenKey)) {
          options.storage.remove(options.token.refreshTokenKey);
        }
      } else {
        if (localStorage.getItem(options.token.accessTokenKey)) {
          localStorage.removeItem(options.token.accessTokenKey);
        }
        if (localStorage.getItem(options.token.refreshTokenKey)) {
          localStorage.removeItem(options.token.refreshTokenKey);
        }
      }
    };

    const uri = getUri();
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      return;
    }

    // use internal fetch function since we can't use the ky client here
    // to avoid circular dependencies

    return ExponentialBackoff(
      async () => {
        const response = await fetch(`${uri}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (response.status === UNAUTHORIZED_STATUS_CODE) {
          deleteTokens();
          return;
        }

        if (isErrorCodeRetryable(response.status)) {
          throw new Error(RETRYABLE_ERROR_MESSAGE);
        }

        if (isErrorCode(response.status)) {
          throw new Error((await response.json()).detail);
        }

        deleteTokens();
      },
      (error) => error.message === RETRYABLE_ERROR_MESSAGE,
      backoff,
    );
  };

  const refreshAccessToken = async (
    backoff?: ExponentialBackoffOptions,
  ): Promise<string> => {
    const uri = getUri();
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // use internal fetch function since we can't use the ky client here
    // to avoid circular dependencies

    return ExponentialBackoff(
      async () => {
        const response = await fetch(`${uri}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        const data = await response.json();

        if (
          response.status === UNAUTHORIZED_STATUS_CODE &&
          (data.detail === SESSION_TOKEN_EXPIRED_DETAIL ||
            data.detail === INVALID_SESSION_TOKEN_DETAIL)
        ) {
          // Session expired, clear session
          await clearSession();
          throw new Error('Session expired');
        }

        if (isErrorCodeRetryable(response.status)) {
          throw new Error(RETRYABLE_ERROR_MESSAGE);
        }

        if (isErrorCode(response.status)) {
          throw new Error((await response.json()).detail);
        }

        if (response.status === NOT_FOUND_STATUS_CODE) {
          // Token not found, clear session
          await clearSession();
          throw new Error('Token not found');
        }

        if (data.accessToken) {
          setAccessToken(data.accessToken);
          return data.accessToken;
        }

        throw new Error('Token refresh failed');
      },
      (error) => error.message === RETRYABLE_ERROR_MESSAGE,
      backoff,
    );
  };

  const pathSkipped = (path: string): boolean =>
    (options.skiplist?.paths ?? []).some((skip) => path.includes(skip)) ??
    false;

  const graphqlSkipped = (query: string): boolean =>
    (options.skiplist?.graphql ?? []).some((skip) => query.includes(skip)) ??
    false;

  return {
    getAccessToken,
    hasAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    authenticate,
    refreshAccessToken,
    clearSession,
    pathSkipped,
    graphqlSkipped,
    headerName: options.headerName,
    // Organization-aware methods
    setOrganizationAuth,
    setCurrentOrganization,
    getCurrentOrganization,
    getAllOrganizations,
  };
};

// Create storage functions - use localStorage for renderer process
const createStorage = () => {
  return {
    get: (key: string): string | null => {
      return localStorage.getItem(key);
    },
    set: (key: string, value: string): void => {
      localStorage.setItem(key, value);
    },
    remove: (key: string): void => {
      localStorage.removeItem(key);
    },
  };
};

// Default auth configuration
export const DEFAULT_AUTH_OPTIONS: FlikHttpAuthOptions = {
  headerName: 'x-authorization',
  token: {
    accessTokenKey: 'auth.accessToken',
    refreshTokenKey: 'auth.refreshToken',
  },
  skiplist: {
    paths: ['/auth/refresh'],
  },
  storage: createStorage(),
  organizationStorage: createOrganizationAuthStorage(),
};
